import { FormEvent, useCallback, useEffect, useState } from "react";
import { api } from "./api";
import type { AppSettings } from "./types";
import "./settings-hub.css";

type FoodPreference = {
  id: string;
  normalizedName: string;
  displayName: string;
  preferenceScore: number;
  satietyScore: number | null;
  convenienceScore: number | null;
  repeatInterest: number | null;
  suitableTimes: string[];
  notes: string | null;
  lastFeedbackAt: string | null;
  createdAt: string;
  updatedAt: string;
};

type AdviceRecord = {
  id: string;
  adviceType: string;
  title: string;
  body: string;
  context: unknown;
  adopted: boolean | null;
  helpfulScore: number | null;
  feedback: string | null;
  createdAt: string;
  updatedAt: string;
};

type PreferenceForm = {
  displayName: string;
  preferenceScore: string;
  satietyScore: string;
  convenienceScore: string;
  repeatInterest: string;
  suitableTimes: string[];
  notes: string;
};

const initialPreference: PreferenceForm = {
  displayName: "",
  preferenceScore: "1",
  satietyScore: "",
  convenienceScore: "",
  repeatInterest: "",
  suitableTimes: [],
  notes: "",
};

const preferenceLabels: Record<number, string> = {
  [-2]: "避けたい",
  [-1]: "苦手",
  0: "どちらでもない",
  1: "好き",
  2: "大好き",
};

const timeLabels: Record<string, string> = {
  breakfast: "朝",
  lunch: "昼",
  dinner: "夜",
  snack: "間食",
};

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function optionalNumber(value: string): number | null {
  return value.trim() === "" ? null : Number(value);
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function AdviceCard({
  advice,
  onSaved,
}: {
  advice: AdviceRecord;
  onSaved: (advice: AdviceRecord) => void;
}) {
  const [adopted, setAdopted] = useState<"" | "yes" | "no">(
    advice.adopted === null ? "" : advice.adopted ? "yes" : "no",
  );
  const [helpfulScore, setHelpfulScore] = useState(advice.helpfulScore?.toString() ?? "");
  const [feedback, setFeedback] = useState(advice.feedback ?? "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  async function saveFeedback() {
    setSaving(true);
    setMessage("");
    try {
      const result = await api<{ ok: true; advice: AdviceRecord }>(
        `/api/app/advice/${encodeURIComponent(advice.id)}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            adopted: adopted === "" ? null : adopted === "yes",
            helpfulScore: helpfulScore === "" ? null : Number(helpfulScore),
            feedback: feedback.trim() || null,
          }),
        },
      );
      onSaved(result.advice);
      setMessage("反応を保存しました。");
    } catch (error) {
      setMessage(errorText(error, "保存できませんでした。"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="advice-card">
      <div className="advice-card-head">
        <div>
          <span>{advice.adviceType}</span>
          <h3>{advice.title}</h3>
        </div>
        <time>{formatDate(advice.createdAt)}</time>
      </div>
      <p className="advice-body">{advice.body}</p>
      <div className="advice-feedback">
        <div className="advice-choice" aria-label="提案を取り入れたか">
          <button type="button" className={adopted === "yes" ? "active" : ""} onClick={() => setAdopted("yes")}>取り入れた</button>
          <button type="button" className={adopted === "no" ? "active negative" : ""} onClick={() => setAdopted("no")}>今回は見送った</button>
          <button type="button" className={adopted === "" ? "active neutral" : ""} onClick={() => setAdopted("")}>未回答</button>
        </div>
        <label className="field">
          <span>役立ち度</span>
          <select value={helpfulScore} onChange={(event) => setHelpfulScore(event.target.value)}>
            <option value="">未評価</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </label>
        <label className="field advice-feedback-text">
          <span>次回に反映したいこと</span>
          <textarea
            rows={2}
            value={feedback}
            maxLength={1000}
            placeholder="量が多かった、準備が面倒だった、これは続けやすい等"
            onChange={(event) => setFeedback(event.target.value)}
          />
        </label>
        <button type="button" className="button secondary" onClick={() => void saveFeedback()} disabled={saving}>
          {saving ? "保存中…" : "反応を保存"}
        </button>
        {message && <small className="advice-save-message">{message}</small>}
      </div>
    </article>
  );
}

export function SettingsHubPage() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [preferences, setPreferences] = useState<FoodPreference[]>([]);
  const [advice, setAdvice] = useState<AdviceRecord[]>([]);
  const [preferenceForm, setPreferenceForm] = useState<PreferenceForm>(initialPreference);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingPreference, setSavingPreference] = useState(false);
  const [deletingPreference, setDeletingPreference] = useState<string | null>(null);
  const [settingsMessage, setSettingsMessage] = useState("");
  const [preferenceMessage, setPreferenceMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [settingsResult, preferenceResult, adviceResult] = await Promise.all([
        api<{ ok: true; settings: AppSettings }>("/api/app/settings"),
        api<{ ok: true; preferences: FoodPreference[] }>("/api/app/preferences"),
        api<{ ok: true; advice: AdviceRecord[] }>("/api/app/advice?limit=40"),
      ]);
      setSettings(settingsResult.settings);
      setPreferences(preferenceResult.preferences);
      setAdvice(adviceResult.advice);
    } catch (loadError) {
      setError(errorText(loadError, "設定を読み込めませんでした。"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateNumber = (key: keyof AppSettings, value: string) => {
    setSettings((current) => current
      ? { ...current, [key]: value === "" ? null : Number(value) }
      : current);
  };

  async function saveSettings(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    setSavingSettings(true);
    setSettingsMessage("");
    try {
      const result = await api<{ ok: true; settings: AppSettings }>("/api/app/settings", {
        method: "PUT",
        body: JSON.stringify(settings),
      });
      setSettings(result.settings);
      setSettingsMessage("目標設定を保存しました。");
    } catch (saveError) {
      setSettingsMessage(errorText(saveError, "保存できませんでした。"));
    } finally {
      setSavingSettings(false);
    }
  }

  function toggleSuitableTime(value: string) {
    setPreferenceForm((current) => ({
      ...current,
      suitableTimes: current.suitableTimes.includes(value)
        ? current.suitableTimes.filter((item) => item !== value)
        : [...current.suitableTimes, value],
    }));
  }

  async function savePreference(event: FormEvent) {
    event.preventDefault();
    if (!preferenceForm.displayName.trim()) {
      setPreferenceMessage("食品・料理名を入力してください。");
      return;
    }
    setSavingPreference(true);
    setPreferenceMessage("");
    try {
      const result = await api<{ ok: true; preference: FoodPreference }>("/api/app/preferences", {
        method: "POST",
        body: JSON.stringify({
          displayName: preferenceForm.displayName.trim(),
          preferenceScore: Number(preferenceForm.preferenceScore),
          satietyScore: optionalNumber(preferenceForm.satietyScore),
          convenienceScore: optionalNumber(preferenceForm.convenienceScore),
          repeatInterest: optionalNumber(preferenceForm.repeatInterest),
          suitableTimes: preferenceForm.suitableTimes,
          notes: preferenceForm.notes.trim() || null,
        }),
      });
      setPreferences((current) => [
        result.preference,
        ...current.filter((item) => item.id !== result.preference.id),
      ]);
      setPreferenceForm(initialPreference);
      setPreferenceMessage("好みを保存しました。専用GPTの提案に反映されます。");
    } catch (saveError) {
      setPreferenceMessage(errorText(saveError, "好みを保存できませんでした。"));
    } finally {
      setSavingPreference(false);
    }
  }

  async function removePreference(item: FoodPreference) {
    if (!window.confirm(`「${item.displayName}」の好み情報を削除しますか？`)) return;
    setDeletingPreference(item.id);
    setPreferenceMessage("");
    try {
      await api(`/api/app/preferences/${encodeURIComponent(item.id)}`, { method: "DELETE" });
      setPreferences((current) => current.filter((value) => value.id !== item.id));
      setPreferenceMessage("好み情報を削除しました。");
    } catch (removeError) {
      setPreferenceMessage(errorText(removeError, "削除できませんでした。"));
    } finally {
      setDeletingPreference(null);
    }
  }

  if (loading) return <div className="notice">設定と履歴を読み込んでいます…</div>;
  if (error) return <div className="notice error">{error}</div>;
  if (!settings) return <div className="notice error">設定情報が見つかりません。</div>;

  return (
    <div className="settings-hub">
      <form className="panel settings-form" onSubmit={saveSettings}>
        <div className="panel-heading">
          <div><p className="eyebrow">GOALS</p><h2>目標設定</h2></div>
          <span className="status-chip">後から変更可能</span>
        </div>
        <div className="form-grid">
          <label className="field">
            <span>アプリ名</span>
            <input value={settings.appName ?? ""} placeholder="未設定" onChange={(event) => setSettings({ ...settings, appName: event.target.value || null })} />
          </label>
          <label className="field">
            <span>目標体重（kg）</span>
            <input type="number" step="0.1" value={settings.targetWeightKg ?? ""} placeholder="未設定" onChange={(event) => updateNumber("targetWeightKg", event.target.value)} />
          </label>
          <label className="field">
            <span>一日カロリー（kcal）</span>
            <input type="number" value={settings.targetCaloriesKcal ?? ""} placeholder="未設定" onChange={(event) => updateNumber("targetCaloriesKcal", event.target.value)} />
          </label>
          <label className="field">
            <span>たんぱく質（g）</span>
            <input type="number" step="0.1" value={settings.targetProteinG ?? ""} placeholder="未設定" onChange={(event) => updateNumber("targetProteinG", event.target.value)} />
          </label>
          <label className="field">
            <span>脂質（g）</span>
            <input type="number" step="0.1" value={settings.targetFatG ?? ""} placeholder="未設定" onChange={(event) => updateNumber("targetFatG", event.target.value)} />
          </label>
          <label className="field">
            <span>炭水化物（g）</span>
            <input type="number" step="0.1" value={settings.targetCarbsG ?? ""} placeholder="未設定" onChange={(event) => updateNumber("targetCarbsG", event.target.value)} />
          </label>
          <label className="field field-wide">
            <span>アドバイスの強さ</span>
            <select value={settings.adviceMode} onChange={(event) => setSettings({ ...settings, adviceMode: event.target.value as AppSettings["adviceMode"] })}>
              <option value="record_only">記録のみ</option>
              <option value="gentle">やさしい助言</option>
              <option value="detailed">しっかり分析</option>
            </select>
          </label>
        </div>
        <button className="button primary" disabled={savingSettings}>{savingSettings ? "保存中…" : "目標設定を保存"}</button>
        {settingsMessage && <p className="form-message">{settingsMessage}</p>}
      </form>

      <section className="panel preference-section">
        <div className="panel-heading">
          <div><p className="eyebrow">FOOD PREFERENCES</p><h2>食の好み・続けやすさ</h2></div>
          <span className="status-chip">{preferences.length}件</span>
        </div>
        <p className="settings-description">好き嫌いだけでなく、満腹感や準備のしやすさも専用GPTの提案に使います。</p>
        <form className="preference-form" onSubmit={savePreference}>
          <div className="preference-grid">
            <label className="field preference-name">
              <span>食品・料理名</span>
              <input value={preferenceForm.displayName} maxLength={120} placeholder="例：オートミール、サラダチキン" onChange={(event) => setPreferenceForm({ ...preferenceForm, displayName: event.target.value })} />
            </label>
            <label className="field">
              <span>好み</span>
              <select value={preferenceForm.preferenceScore} onChange={(event) => setPreferenceForm({ ...preferenceForm, preferenceScore: event.target.value })}>
                <option value="2">大好き</option>
                <option value="1">好き</option>
                <option value="0">どちらでもない</option>
                <option value="-1">苦手</option>
                <option value="-2">避けたい</option>
              </select>
            </label>
            <label className="field">
              <span>満腹感 1〜5</span>
              <select value={preferenceForm.satietyScore} onChange={(event) => setPreferenceForm({ ...preferenceForm, satietyScore: event.target.value })}>
                <option value="">未設定</option>
                {[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <label className="field">
              <span>準備の楽さ 1〜5</span>
              <select value={preferenceForm.convenienceScore} onChange={(event) => setPreferenceForm({ ...preferenceForm, convenienceScore: event.target.value })}>
                <option value="">未設定</option>
                {[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <label className="field">
              <span>また食べたい 1〜5</span>
              <select value={preferenceForm.repeatInterest} onChange={(event) => setPreferenceForm({ ...preferenceForm, repeatInterest: event.target.value })}>
                <option value="">未設定</option>
                {[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <fieldset className="preference-times">
              <legend>向いている時間帯</legend>
              <div>
                {Object.entries(timeLabels).map(([value, label]) => (
                  <label key={value}>
                    <input type="checkbox" checked={preferenceForm.suitableTimes.includes(value)} onChange={() => toggleSuitableTime(value)} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="field preference-notes">
              <span>メモ</span>
              <textarea rows={3} maxLength={1000} value={preferenceForm.notes} placeholder="量、価格、調理負担、食べ方など" onChange={(event) => setPreferenceForm({ ...preferenceForm, notes: event.target.value })} />
            </label>
          </div>
          <button className="button primary" disabled={savingPreference}>{savingPreference ? "保存中…" : "好みを追加・更新"}</button>
          {preferenceMessage && <p className="form-message">{preferenceMessage}</p>}
        </form>

        {preferences.length === 0 ? (
          <div className="preference-empty">まだ登録がありません。普段よく食べるものから一件だけで大丈夫です。</div>
        ) : (
          <div className="preference-list">
            {preferences.map((item) => (
              <article key={item.id}>
                <div className="preference-main">
                  <div><h3>{item.displayName}</h3><span className={`preference-score score-${item.preferenceScore}`}>{preferenceLabels[item.preferenceScore] ?? "未評価"}</span></div>
                  {item.notes && <p>{item.notes}</p>}
                  <div className="preference-tags">
                    {item.satietyScore !== null && <span>満腹感 {item.satietyScore}</span>}
                    {item.convenienceScore !== null && <span>楽さ {item.convenienceScore}</span>}
                    {item.repeatInterest !== null && <span>再食 {item.repeatInterest}</span>}
                    {item.suitableTimes.map((time) => <span key={time}>{timeLabels[time] ?? time}</span>)}
                  </div>
                </div>
                <button type="button" disabled={deletingPreference === item.id} onClick={() => void removePreference(item)}>{deletingPreference === item.id ? "削除中…" : "削除"}</button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="panel advice-section">
        <div className="panel-heading">
          <div><p className="eyebrow">ADVICE HISTORY</p><h2>アドバイス履歴</h2></div>
          <span className="status-chip">{advice.length}件</span>
        </div>
        <p className="settings-description">専用GPTから受けた提案と、その提案が続けやすかったかを残します。</p>
        {advice.length === 0 ? (
          <div className="advice-empty">専用GPTからアドバイスを受けると、ここに履歴が表示されます。</div>
        ) : (
          <div className="advice-list">
            {advice.map((item) => (
              <AdviceCard
                key={item.id}
                advice={item}
                onSaved={(saved) => setAdvice((current) => current.map((value) => value.id === saved.id ? saved : value))}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
